import { IKnmRequirementViolation } from 'APIPackage/KnmModule/interfaces/IKnmRequirementViolation';
import {
  IKnmErknmFormViolation,
  IKnmErknmFormViolationEvent,
  IViolationFixPlanDateItem,
  IViolationFixPlanExplanationItem,
  IViolationMaterialTypeItem,
} from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { createViolationMaterialTypeSelectOption } from 'CorePackage/CatalogsModule/common/createViolationMaterialTypeSelectOption';
import { convertInitialKnmDateToFormDate } from 'KnmPackage/MainModule/common/convertInitialKnmDateToFormDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { createViolationFixMethodSelectOption } from 'CorePackage/CatalogsModule/common/createViolationFixMethodSelectOption';
import { convertKnmDataViolationFixesToFormValues } from 'KnmErknmPackage/MainModule/common/convertKnmDataViolationFixesToFormValues';
import { createTypeDocConfirmationViolationSelectOption } from 'CorePackage/CatalogsModule/common/createTypeDocConfirmationViolationSelectOption';
import { createPassportEventSelectOptions } from 'KnmErknmPackage/ErknmViolationsModule/common/createPassportEventSelectOptions';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { IKnmFormAttachment } from 'KnmPackage/MainModule/interfaces/IKnmFormAttachment';
import { createVideoFixationSelectOptionByGuid } from 'KnmErknmPackage/ErknmViolationsModule/common/createVideoFixationSelectOptions';

import { handleReactQuillDeltaValue } from '../../../functions/handleReactQuillDeltaValue';

const negativeSelectOption = booleanSelectOptions.find(option => !option.value);
const positiveSelectOption = booleanSelectOptions.find(option => option.value);

function convertViolationEventMaterialAttachmentsToFormValues(
  attachments: IKnmAttachment[] | null | undefined,
): IKnmFormAttachment[] | null {
  if (!attachments?.length) {
    return null;
  }

  return attachments.map(attachmentItem => ({
    ...attachmentItem,
    isNew: false,
  }));
}

export function convertKnmDataViolationsToFormValues(
  violationsData: IKnmRequirementViolation[],
  makeAsNew?: boolean,
  passportEvents?: IKnmErknmFormEvent[],
  videoFixations?: Array<{ guid: string | null }> | null,
): IKnmErknmFormViolation[] {
  const passportEventOptions = createPassportEventSelectOptions(passportEvents);

  return violationsData.map((violation, index) => {
    const isInEvent = typeof violation.inEvent === 'boolean' ? violation.inEvent : false;

    return {
      id: violation.guid,
      isNew: makeAsNew || !!violation.isNew || false,
      status: violation.status || null,
      title: violation.description,
      descriptionReactQuillEditor: handleReactQuillDeltaValue(
        violation.descriptionReactQuillEditor || violation.description || null,
      ),
      isUnfixable:
        booleanSelectOptions.find(option => option.value === violation.notRemoval) || negativeSelectOption || null,
      isFixedOnPlace:
        booleanSelectOptions.find(option => option.value === violation.correctedInEvent) ||
        negativeSelectOption ||
        null,
      explanation: violation.explanation,
      explanationReactQuillEditor: handleReactQuillDeltaValue(
        violation.explanationReactQuillEditor || violation.explanation,
      ),
      isFixed:
        booleanSelectOptions.find(option => option.value === violation.corrected) || negativeSelectOption || null,
      isAppealed:
        booleanSelectOptions.find(option => option.value === violation.appealed) || negativeSelectOption || null,

      fixPlanId: violation.remediationPlan?.guid || null,
      fixPlanDates: violation.remediationPlan?.plannedEliminationDates?.length
        ? violation.remediationPlan.plannedEliminationDates.map<IViolationFixPlanDateItem>(dateItem => ({
            ...dateItem,
            isNew: !!dateItem.isNew,
            value: convertInitialKnmDateToFormDate(dateItem.value, DATE_FORMAT_LONG_FNS) || null,
          }))
        : null,
      fixPlanMethods: violation.remediationPlan?.plannedMethod?.length
        ? violation.remediationPlan.plannedMethod.map<IViolationMaterialTypeItem>(methodDTO => ({
            isNew: !!methodDTO.isNew,
            value: createViolationMaterialTypeSelectOption(methodDTO),
          }))
        : null,
      fixPlanExplanations: violation.remediationPlan?.descriptions?.length
        ? violation.remediationPlan.descriptions.map<IViolationFixPlanExplanationItem>(explanationItem => ({
            ...explanationItem,
            isNew: !!explanationItem.isNew,
            value: explanationItem.value,
            valueReactQuillEditor: handleReactQuillDeltaValue(
              explanationItem.valueReactQuillEditor || explanationItem.value,
            ),
          }))
        : null,

      fixFactDate: convertInitialKnmDateToFormDate(violation.actualEliminationDate, DATE_FORMAT_LONG_FNS) || null,
      fixFactMethod: violation.actualMethod ? createViolationFixMethodSelectOption(violation.actualMethod) : null,

      fixes: violation.eliminationViolations?.length
        ? convertKnmDataViolationFixesToFormValues(violation.eliminationViolations)
        : null,

      updatedBy: violation.updatedBy,
      updated: violation.updated,

      isViolationResolved: booleanSelectOptions.find(item => item.value === violation.isViolationResolved) || undefined,
      isViolationResolvedNote: violation.isViolationResolvedNote || undefined,
      dateResolvedByAgreement:
        convertInitialKnmDateToFormDate(violation.dateResolvedByAgreement, DATE_FORMAT_LONG_FNS) || undefined,
      previousDateResolvedByAgreement:
        convertInitialKnmDateToFormDate(violation.previousDateResolvedByAgreement, DATE_FORMAT_LONG_FNS) || undefined,
      includeInSubject: typeof violation.includeInSubject === 'boolean' ? violation.includeInSubject : undefined,
      prescriptionLink: violation.prescriptionLink || undefined,
      number: violation.number || String(index + 1),
      repeatCorrected: booleanSelectOptions.find(option => option.value === violation.repeatCorrected) || null,
      repeatCorrectedInEvent:
        booleanSelectOptions.find(option => option.value === violation.repeatCorrectedInEvent) ||
        negativeSelectOption ||
        null,
      repeatExplanation: violation.repeatExplanation,
      targetLink: violation.targetLink || undefined,
      targetErpId: violation.targetErpId || undefined,
      resolvedIn: violation.resolvedIn || undefined,
      inEvent: isInEvent ? positiveSelectOption : negativeSelectOption,
      events: isInEvent
        ? violation.events?.map(event => {
            const eventGuidValue = event.event?.guid || null;
            const eventOption = passportEventOptions.find(option => option.value === eventGuidValue) || null;

            const result: IKnmErknmFormViolationEvent = {
              guid: event.guid,
              eventOption,
              isNew: false,
              materials:
                event.materials?.map(material => ({
                  guid: material.guid,
                  isNew: false,
                  typeDocConfirmationViolation: material.typeDocConfirmationViolation
                    ? createTypeDocConfirmationViolationSelectOption(material.typeDocConfirmationViolation)
                    : null,
                  attachments: convertViolationEventMaterialAttachmentsToFormValues(material.attachments),
                  links: material.links?.length
                    ? material.links.map(link => ({
                        id: link.id,
                        value: link.value,
                      }))
                    : null,
                  videoFixations: material.videoFixations?.length
                    ? material.videoFixations.map(linkedVideoFixation => ({
                        guid: linkedVideoFixation.guid,
                        videoFixationOption: linkedVideoFixation.videoFixationsGuid
                          ? createVideoFixationSelectOptionByGuid(
                              linkedVideoFixation.videoFixationsGuid,
                              videoFixations,
                            )
                          : null,
                      }))
                    : null,
                })) || null,
            };

            return result;
          })
        : undefined,
    };
  });
}
